import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banknote, CreditCard, Diamond, PenLine, ShieldCheck, Undo2, Wallet } from "lucide-react";

import { api } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { AdminPage, Card, ChipRow, Gate, KV, Meta, Pager, ReasonDialog, StatCard, StatGrid } from "@/components/admin-ui";
import { Button, EmptyState, ErrorState, Field, Loading, Pill } from "@/components/ui";

const TYPES = [{ id: "", label: "All types" }, { id: "membership", label: "Membership" }, { id: "verification", label: "Verification" }];
const FILTERS = [
  { id: "", label: "All" }, { id: "success", label: "Success" }, { id: "failed", label: "Failed" },
  { id: "refund_flagged", label: "Refund flagged" }, { id: "refund_requested", label: "Refund requested" }, { id: "refunded", label: "Refunded" },
];
const tone = (s) => (s === "success" ? "success" : s === "failed" ? "error" : "neutral");
const PROVIDERS = { paystack: "Paystack", test: "Test", google_play: "Google Play", app_store: "App Store" };
const isStore = (p) => p === "google_play" || p === "app_store";

export default function AdminPayments() {
  return <Gate perm="payments" testId="admin-payments-forbidden"><Inner /></Gate>;
}

function Inner() {
  const toast = useToast();
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin-payments", status, type, page],
    queryFn: () => api(`/admin/payments?page=${page}${status ? `&status=${status}` : ""}${type ? `&type=${type}` : ""}`),
  });
  const p = data?.payments.find((x) => x.reference === sel) ?? null;
  const t = data?.totals ?? {};
  const run = async (fn, msg) => {
    try { await fn(); toast(msg, "success"); setMode(null); setAmount(""); setReason(""); refetch(); } catch (e) { toast(e.message, "error"); }
  };
  const pendingRefund = p?.adjustments.find((a) => a.kind === "refund" && a.status === "refund_requested");
  const amountOk = /^-?\d+$/.test(amount) && parseInt(amount, 10) !== 0;

  return (
    <AdminPage title="Payments" testId="admin-payments-screen">
      <ChipRow options={TYPES} value={type} onChange={(v) => { setType(v); setPage(1); }} testIdPrefix="admin-payments-type" />
      <ChipRow options={FILTERS} value={status} onChange={(v) => { setStatus(v); setPage(1); }} testIdPrefix="admin-payments-status" />
      {data?.test_mode ? <div><Pill testId="admin-payments-test-mode" label="TEST MODE — no real money moves" tone="gold" /></div> : null}
      <StatGrid>
        <StatCard testId="payments-gross" icon={Banknote} label={`Successful (${t.count_success ?? 0})`} value={fmtMoney(t.gross_success)} />
        <StatCard testId="payments-refunded" icon={Undo2} label={`Refunded (${t.count_refunded ?? 0})`} value={fmtMoney(t.refunded)} />
        <StatCard testId="payments-corrections" icon={PenLine} label="Corrections" value={fmtMoney(t.corrections)} />
        <StatCard testId="payments-membership-revenue" icon={Diamond} label="Membership revenue" value={fmtMoney(t.membership_revenue)} />
        <StatCard testId="payments-verification-revenue" icon={ShieldCheck} tone="gold" label="Verification revenue" value={fmtMoney(t.verification_revenue)} />
        <StatCard testId="payments-net" icon={Wallet} label={`Net · ${t.count_failed ?? 0} failed · ${t.count_refund_flagged ?? 0} flagged`} value={fmtMoney(t.net)} />
      </StatGrid>
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-payments-error" message={error.message} onRetry={() => refetch()} /> : data.payments.length ? (
        <>
          {data.payments.map((x) => (
            <Card key={x.reference} testId={`admin-payment-row-${x.reference}`} onClick={() => setSel(x.reference)}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-mono text-sm font-bold">{x.reference}</p>
                  <Meta>{x.user.display_name} · {x.type === "membership" ? `Membership ${x.plan_name ?? ""}` : "Verification"} · {x.method} · {fmtDate(x.created_at)}</Meta>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="font-display text-xl font-bold">{fmtMoney(x.amount, x.currency)}</span>
                  <span className="flex flex-wrap justify-end gap-1"><Pill testId={`admin-payment-provider-${x.reference}`} label={PROVIDERS[x.provider] ?? x.provider} tone={isStore(x.provider) ? "rose" : "neutral"} /><Pill label={x.status} tone={tone(x.status)} />{x.refund_status ? <Pill testId={`admin-payment-refund-${x.reference}`} label={x.refund_status.replace("_", " ")} tone="gold" /> : null}</span>
                </div>
              </div>
            </Card>
          ))}
          <Pager testId="admin-payments-pager" page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      ) : <EmptyState testId="admin-payments-empty" icon={CreditCard} title="No transactions" />}
      <Modal open={!!p && !mode} onClose={() => setSel(null)} title={p?.reference ?? ""} testId="admin-payment-sheet">
        {p ? (
          <>
            <KV k="Member" v={`${p.user.display_name} (${p.user.email ?? ""})`} />
            <KV k="Type" v={p.type === "membership" ? `Membership · ${p.plan_name ?? ""}` : "Verification"} />
            <KV k="Amount" v={fmtMoney(p.amount, p.currency)} />
            <KV k="Method / provider" v={`${p.method} / ${PROVIDERS[p.provider] ?? p.provider}`} testId="admin-payment-provider" />
            <KV k="Status" v={p.status} testId="admin-payment-original-status" />
            <KV k="Completed" v={fmtDate(p.completed_at)} />
            {p.duplicate ? <KV k="Duplicate charge" v="Yes — refund due" /> : null}
            <p className="my-3 text-xs text-muted-foreground">Original records are immutable. Fixes are added as linked entries below.</p>
            {p.adjustments.map((a) => (
              <div key={a.id} className="mb-2 rounded-xl bg-[#FDFBF7] p-3" data-testid={`admin-adjustment-${a.reference}`}>
                <p className="text-sm font-bold">{a.kind === "refund" ? "Refund" : "Correction"} {a.reference}</p>
                <Meta>{fmtMoney(a.amount, a.currency)} · {a.status.replace("_", " ")} · {a.actor_name} · {fmtDate(a.created_at)}</Meta>
                <Meta>{a.reason}</Meta>
              </div>
            ))}
            <div className="mt-4 flex flex-col gap-2">
              {isStore(p.provider) ? (
                <p className="rounded-xl bg-[#F6E3E4] p-3 text-sm" data-testid="admin-payment-store-refund-note">
                  {PROVIDERS[p.provider]} purchase — refunds are issued in {p.provider === "google_play" ? "Google Play Console" : "App Store Connect"}. The store notifies LOVE NET automatically and membership is updated.
                </p>
              ) : pendingRefund ? (
                <Button testId="admin-payment-process-refund" title={`Process refund (${PROVIDERS[p.provider] ?? p.provider})`} onClick={() => run(() => api(`/admin/refunds/${pendingRefund.id}/process`, { method: "POST" }), "Refund processed")} />
              ) : p.status === "success" && p.refund_status !== "refunded" ? (
                <Button testId="admin-payment-request-refund" title="Request refund" variant="outline" onClick={() => setMode("refund")} />
              ) : null}
              <Button testId="admin-payment-add-correction" title="Add correction entry" variant="outline" onClick={() => setMode("correction")} />
            </div>
          </>
        ) : null}
      </Modal>
      <ReasonDialog open={mode === "refund"} testId="admin-refund-sheet" title="Request refund" confirmLabel="Request refund" minLength={5} onClose={() => setMode(null)}
        onConfirm={(r) => run(() => api(`/admin/payments/${sel}/refund`, { method: "POST", body: { reason: r } }), "Refund requested")} />
      <Modal open={mode === "correction"} onClose={() => setMode(null)} title="Correction entry" testId="admin-correction-sheet">
        <Field testId="admin-correction-amount" label="Amount (use - for a reduction)" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="-500" />
        <Field testId="admin-correction-reason" label="Reason" multiline value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this correction needed?" />
        <Button testId="admin-correction-submit" title="Add correction" className="w-full" disabled={!amountOk || reason.trim().length < 5}
          onClick={() => run(() => api(`/admin/payments/${sel}/corrections`, { method: "POST", body: { amount: parseInt(amount, 10), reason: reason.trim() } }), "Correction added")} />
      </Modal>
    </AdminPage>
  );
}
